import React, { useEffect, useState } from 'react';
import { Filter } from '@components';
import { Button } from '@primitives';
import styles from './styles.module.scss';
import VerticalSeperator from '../../../../components/VerticalSeperator';
import { DownloadIcon } from '@icons';
import { UploadIcon } from '@icons';
import axiosInstance, { ApiKeys, getApiUrl } from '../../../../utils/ApiHelper';
import { Agency, Downloadtype, State } from '@cp/pages/AgencyPincodeMapping/types';
import { useSelector } from 'react-redux';
import SingleAutocompleteDropdown from '../../../../components/AutocompleteDropdown/SingleAutocompleteDropdown';
import { useDispatch } from 'react-redux';
import { setAgencies, setSelectedAgency, setSelectedState } from '@cp/reducers/agencyCappingSlice';
import { RootState } from '../../../../store';
import { toast } from '@primitives/Toast';
import toProperCase from '@cp/src/utils/toProperCase';
import { fetchFiltersData } from '@cp/pages/AgencyCapping/Actions';
import { AGENCY_CAPPING_DOWNLOAD_TYPE } from '@cp/pages/AgencyCapping/types';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { AGENCY_CAPPING_CLICKSTREAM } from '@cp/src/service/clickStream.constant';

type DataTableHeaderProps = {
  openUploadDialog: () => void;
};

export const transformStates = (states: any = []) => {
  return states.map((state: string) => {
    return {
      label: toProperCase(state?.toLowerCase()),
      value: state
    };
  });
};

export const transformAgencies = (agencies: any = []) => {
  return agencies.map((agency: Agency) => {
    return {
      label: agency.name,
      value: agency.code
    };
  });
};

function Controls({ openUploadDialog }: DataTableHeaderProps) {
  const selectedAgency = useSelector((state: RootState) => state.agencyCapping.selectedAgency);
  const selectedState = useSelector((state: RootState) => state.agencyCapping.selectedState);

  const states = useSelector((state: RootState) => state.agencyCapping.states);
  const agencies = useSelector((state: RootState) => state.agencyCapping.agencies);
  const dispatch = useDispatch();
  const [filtersLoader, setFiltersLoader] = useState(true);

  useEffect(() => {
    dispatch(fetchFiltersData(setFiltersLoader));
  }, []);

  const onMyServiceabilityDownload = () => {
    addClickstreamEvent(AGENCY_CAPPING_CLICKSTREAM.LH_CLICK_DOWNLOAD_CURRENT_CAPPING_BUTTON);
    const url = getApiUrl(ApiKeys.AGENCY_CAPPING_SERVICEABILITY_DOWNLOAD);
    axiosInstance
      .get(url, {
        params: { agencyCappingDownloadType: AGENCY_CAPPING_DOWNLOAD_TYPE.CURRENT_CAPPING }
      })
      .then(res => {
        window.open(res?.data?.preSignedGetUrl, '_blank');
      })
      .catch(err => {
        toast('Unable to download file please try again', { type: 'error' });
      });
  };

  const handleOnAgencyChange = (agency: string) => {
    addClickstreamEvent(AGENCY_CAPPING_CLICKSTREAM.LH_SELECT_AGENCY_FROM_DROPDOWN_AGENCY_CAPPING, {
      agencyId: agency
    });
    dispatch(setSelectedAgency(agency));
  };

  const handleOnStateChange = (state: string) => {
    addClickstreamEvent(AGENCY_CAPPING_CLICKSTREAM.LH_SELECT_STATE_FROM_DROPDOWN_AGENCY_CAPPING, {
      agencyState: state
    });
    dispatch(setSelectedState(state));
  };

  return (
    <div className="flex justify-between">
      <div className="flex gap-4">
        <SingleAutocompleteDropdown
          key={'agencies'}
          placeholder={'Select Agency'}
          selectedOption={selectedAgency}
          options={transformAgencies(agencies)}
          onSelectionChange={option => handleOnAgencyChange(option?.value || '')}
          containerClasses={styles.dropdown}
          clickstreamEvent={
            AGENCY_CAPPING_CLICKSTREAM.LH_CLICK_SELECT_AGENCY_DROPDOWN_AGENCY_CAPPING
          }
        />
        <SingleAutocompleteDropdown
          title={toProperCase(selectedState?.toLowerCase()) || 'Filter state'}
          onSelectionChange={option => handleOnStateChange(option?.value || '')}
          options={transformStates(states)}
          selectedOption={selectedState || ''}
          containerClasses={styles.dropdown}
          placeholder={toProperCase(selectedState?.toLowerCase()) || 'Filter state'}
          clickstreamEvent={
            AGENCY_CAPPING_CLICKSTREAM.LH_CLICK_FILTER_STATE_DROPDOWN_AGENCY_CAPPING
          }
        />
      </div>

      <div className="flex gap-4 items-center">
        <Button
          onClick={onMyServiceabilityDownload}
          variant="secondary"
          startAdornment={<DownloadIcon color={'var(--grayscale-2)'} />}
          color={'var(--grayscale-2)'}
        >
          Download current capping
        </Button>
        <Button
          onClick={openUploadDialog}
          variant="primary"
          startAdornment={<UploadIcon color="white" />}
        >
          Update agency capping
        </Button>
      </div>
    </div>
  );
}

export default Controls;
